import {
  useState,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import { produce } from 'immer';

/**
 * useImmer allows you to modify a draft state via setState via patches that will then
 * apply it to the next state.
 * @example
 * const [state, setState] = useImmer({ id: 2, description: 'hello' });
 *
 * // Since we are actually interacting with an Immer draft state, we can modify the
 * // draft directly
 * setState(draft => {
 *  draft.id = 4;
 * });
 *
 * // state = { id: 4, description: 'hello' };
 */
export function useImmer(defaultState) {
  const [state, setState] = useState(defaultState);

  const applyPatches = useCallback(patches => setState(s => produce(s, patches)), []);

  return [state, applyPatches];
}

/**
 * useImmerReducer is useReducer that utilizes useReducer and Immer under the hood.
 * It allows you to use Immer-style reducers. See https://immerjs.github.io/immer/docs/example-reducer
 * for more details
 *
 * * @example
 * const reducer = (draft, action) => {
 *  switch(action.type) {
 *    ...
 *    case 'CHANGE_ID': {
 *      draft.id = action.payload;
 *    }
 *    ...
 *  }
 * };
 *
 * const [state, dispatch] = useImmerReducer(reducer, { id: 3 });
 * ....
 *
 */
export function useImmerReducer(reducer, defaultState) {
  const memoizedReducer = useMemo(() => produce(reducer), [reducer]);
  return useReducer(memoizedReducer, defaultState);
}
